import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiService } from '../services/api';

const AppSettingsContext = createContext();

export const useAppSettings = () => {
    const context = useContext(AppSettingsContext);
    if (!context) {
        throw new Error('useAppSettings must be used within an AppSettingsProvider');
    }
    return context;
};

export const AppSettingsProvider = ({ children }) => {
    const [settings, setSettings] = useState({
        'app.name': 'WhatsApp Agent Chat',
        'app.description': 'Professional WhatsApp Business API Management Platform',
        'app.url': '',
        'app.terms_url': '',
        'app.privacy_url': '',
        'app.support_email': '',
        'app.date_format': 'YYYY-MM-DD',
        'app.time_format': '24h',
        'app.timezone': 'UTC',
        'app.language': 'en',
        'app.currency': 'USD',
        'app.logo_url': '',
        'app.favicon_url': ''
    });
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadAppSettings();
    }, []);

    const loadAppSettings = async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Try public endpoint first (for non-authenticated access)
            let response;
            try {
                response = await apiService.getPublicAppSettings();
            } catch (publicError) {
                // If public endpoint fails, try authenticated endpoint
                try {
                    response = await apiService.getAppSettings();
                } catch (authError) {
                    throw publicError; // Throw the original public error
                }
            }
            
            if (response && response.settings) {
                setSettings(prev => ({
                    ...prev,
                    ...response.settings
                }));
            }
        } catch (err) {
            console.error('Failed to load app settings:', err);
            setError(err.message);
            // Continue with default settings if API fails
        } finally {
            setLoading(false);
        }
    };

    const updateSettings = async (newSettings) => {
        try {
            await apiService.updateAppSettings(newSettings);
            setSettings(prev => ({
                ...prev,
                ...newSettings
            }));
            return true;
        } catch (err) {
            console.error('Failed to update app settings:', err);
            throw err;
        }
    };

    const refreshSettings = () => {
        return loadAppSettings();
    };

    // Helper functions for common formatting needs
    const formatDate = (date, customFormat = null) => {
        if (!date) return '';
        
        const dateObj = new Date(date);
        const format = customFormat || settings['app.date_format'];
        
        const options = {
            timeZone: settings['app.timezone']
        };

        switch (format) {
            case 'DD/MM/YYYY':
                return dateObj.toLocaleDateString('en-GB', options);
            case 'MM/DD/YYYY':
                return dateObj.toLocaleDateString('en-US', options);
            case 'DD-MM-YYYY':
                return dateObj.toLocaleDateString('en-GB', options).replace(/\//g, '-');
            case 'MMM DD, YYYY':
                return dateObj.toLocaleDateString('en-US', { 
                    ...options,
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                });
            case 'MMMM DD, YYYY':
                return dateObj.toLocaleDateString('en-US', { 
                    ...options,
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                });
            case 'YYYY-MM-DD':
            default:
                return dateObj.toISOString().split('T')[0];
        }
    };

    const formatTime = (date, customFormat = null) => {
        if (!date) return '';
        
        const dateObj = new Date(date);
        const format = customFormat || settings['app.time_format'];
        
        const options = {
            timeZone: settings['app.timezone']
        };

        if (format === '12h') {
            return dateObj.toLocaleTimeString('en-US', {
                ...options,
                hour12: true,
                hour: 'numeric',
                minute: '2-digit'
            });
        } else {
            return dateObj.toLocaleTimeString('en-US', {
                ...options,
                hour12: false,
                hour: '2-digit',
                minute: '2-digit'
            });
        }
    };

    const formatDateTime = (date, customDateFormat = null, customTimeFormat = null) => {
        if (!date) return '';
        
        const formattedDate = formatDate(date, customDateFormat);
        const formattedTime = formatTime(date, customTimeFormat);
        
        return `${formattedDate} ${formattedTime}`;
    };

    const formatCurrency = (amount, customCurrency = null) => {
        if (amount === null || amount === undefined) return '';
        
        const currency = customCurrency || settings['app.currency'];
        const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
        
        try {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: currency
            }).format(numericAmount);
        } catch (err) {
            return `${currency} ${numericAmount.toFixed(2)}`;
        }
    };

    // Getter functions for easy access
    const getAppName = () => settings['app.name'];
    const getAppDescription = () => settings['app.description'];
    const getAppUrl = () => settings['app.url'];
    const getTermsUrl = () => settings['app.terms_url'];
    const getPrivacyUrl = () => settings['app.privacy_url'];
    const getSupportEmail = () => settings['app.support_email'];
    const getLogoUrl = () => settings['app.logo_url'];
    const getFaviconUrl = () => settings['app.favicon_url'];
    const getLanguage = () => settings['app.language'];
    const getTimezone = () => settings['app.timezone'];

    const value = {
        settings,
        loading,
        error,
        updateSettings,
        refreshSettings,
        
        // Helper functions
        formatDate,
        formatTime,
        formatDateTime,
        formatCurrency,
        
        // Getter functions
        getAppName,
        getAppDescription,
        getAppUrl,
        getTermsUrl,
        getPrivacyUrl,
        getSupportEmail,
        getLogoUrl,
        getFaviconUrl,
        getLanguage,
        getTimezone
    };

    return (
        <AppSettingsContext.Provider value={value}>
            {children}
        </AppSettingsContext.Provider>
    );
};

export default AppSettingsContext;